import { useState, useEffect, useMemo } from 'react';
import { useSearchQuoteStatusChange } from '@services/quotes';
import { useSearchQuoteRatings } from '@services/quoteRating/useSearchQuoteRatings';
import { useSearchNotes } from '@services/note';
import { useSearchDocumentsInfinite } from '@services/document';
import { useQuote } from '@services/quotes';
import { DocumentType } from '@models/document';
import { useQuoteActivityData } from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/getQuoteActivityData';
import {
  IFiltering,
  ISorting,
  Sorting,
  externalFilterInitialState,
  internalFilterInitialState,
} from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivity.constants';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useSearchUnderwritingActivity } from '@services/underwritingReferral/useSearchUnderwritingActivity';

export interface UseQuoteActivityHelperReturn {
  quoteId: string;
  quote: any;
  sorting: ISorting;
  filter: IFiltering;
  textInput: string;
  isScrolledToBottom: boolean;
  isLoading: boolean;
  isFetchingMore: boolean;
  allStatusChanges: any[];
  allQuoteRatings: any[];
  allNotes: any[];
  allDocuments: any[];
  items: any[];
  dateHeaderItems: any;
  lastLoadedItems: any;
  setSorting: (sorting: ISorting) => void;
  setFilter: (filter: IFiltering) => void;
  setTextInput: (text: string) => void;
  setIsScrolledToBottom: (isScrolled: boolean) => void;
  fetchMoreStatusChanges: () => void;
  fetchMoreQuoteRatings: () => void;
  fetchMoreDocuments: () => void;
  fetchMoreNotes: () => void;
}

export const useQuoteActivityHelper = (quoteId: string): UseQuoteActivityHelperReturn => {
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);

  const [sorting, setSorting] = useState<ISorting>({
    type: Sorting.Newest,
    anchorEl: null,
  });

  const [filter, setFilter] = useState<IFiltering>({
    list: isInternalUser ? internalFilterInitialState : externalFilterInitialState,
    anchorEl: null,
  });

  const [textInput, setTextInput] = useState<string>('');
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  const { quote } = useQuote(quoteId);

  const {
    quoteStatusChange,
    isLoading: isQuoteStatusChangeLoading,
    isFetching: isFetchingMoreStatusChanges,
    fetchNextPage: fetchMoreStatusChanges,
  } = useSearchQuoteStatusChange({
    quoteId,
    pageLimit: 10,
    sort: sorting.type === Sorting.Newest ? '-createdAt' : '+createdAt',
  });

  const {
    data: searchQuoteRatings,
    isLoading: isQuoteRatingsLoading,
    isFetching: isFetchingMoreQuoteRatings,
    fetchNextPage: fetchMoreQuoteRatings,
  } = useSearchQuoteRatings({
    quoteId,
    enabled: isInternalUser,
    params: {
      hasRateChangeAnalysis: false,
      pageLimit: 10,
      sort: sorting.type === Sorting.Newest ? '-createdAt' : '+createdAt',
    },
  });

  const {
    searchDocuments,
    isLoading: isDocumentsLoading,
    isFetchingNextPage: isFetchingMoreDocuments,
    fetchNextPage: fetchMoreDocuments,
  } = useSearchDocumentsInfinite(
    {
      quoteId: quoteId as string,
      documentTypeId: [DocumentType.QuoteProposal.id, DocumentType.Application.id],
      documentGenerationStatus: 'Completed',
      pageLimit: 10,
      sort: sorting.type === Sorting.Newest ? '-createdAt' : '+createdAt',
    },
    isInternalUser,
  );

  const {
    searchNotes,
    isLoading: isSearchNotesLoading,
    isFetchingNextPage: isFetchingMoreNotes,
    fetchNextPage: fetchMoreNotes,
  } = useSearchNotes({
    quoteId: quoteId as string,
    pageLimit: 10,
    sort: sorting.type === Sorting.Newest ? '-createdAtOrReplacedAt,-createdAt' : '+createdAtOrReplacedAt,+createdAt',
    includeDeleted: true,
  });

  // FUTURE FULLSCREEN TODO this entire api logic is duplicate of QuoteActivity and is requiring everything to be done twice, it must be combined/refactored
  // seems like this was made to be reusable but never was implemented for the sidebar
  const {
    underwritingActivity: quoteUnderwritingActivity,
    isLoading: isQuoteUnderwritingActivityLoading,
    isFetching: isFetchingMoreQuoteUnderwritingActivity,
    fetchNextPage: fetchMoreQuoteUnderwritingActivity,
  } = useSearchUnderwritingActivity({
    quoteId,
    pageLimit: 10,
    sort: sorting.type === Sorting.Newest ? '-createdAt' : '+createdAt',
  });

  const isLoading =
    isQuoteStatusChangeLoading ||
    isQuoteRatingsLoading ||
    isSearchNotesLoading ||
    isDocumentsLoading ||
    isQuoteUnderwritingActivityLoading;
  const isFetchingMore =
    isFetchingMoreStatusChanges ||
    isFetchingMoreQuoteRatings ||
    isFetchingMoreNotes ||
    isFetchingMoreDocuments ||
    isFetchingMoreQuoteUnderwritingActivity;

  const allStatusChanges = useMemo(
    () => quoteStatusChange?.pages.flatMap((page) => page.items) || [],
    [quoteStatusChange?.pages],
  );
  const allQuoteRatings = useMemo(
    () => searchQuoteRatings?.pages.flatMap((page) => page.items) || [],
    [searchQuoteRatings?.pages],
  );
  const allNotes = useMemo(() => searchNotes?.pages?.flatMap((page) => page.items) || [], [searchNotes?.pages]);
  const allDocuments = useMemo(
    () => searchDocuments?.pages?.flatMap((page: any) => page.items) || [],
    [searchDocuments?.pages],
  );

  const allQuoteUnderwritingActivity = useMemo(
    () => quoteUnderwritingActivity?.pages.flatMap((page) => page.items) || [],
    [quoteUnderwritingActivity?.pages],
  );

  const { items, dateHeaderItems, lastLoadedItems } = useQuoteActivityData(
    {
      allStatusChanges,
      allQuoteRatings,
      allNotes,
      allDocuments,
      allQuoteUnderwritingActivity,
      quote: quote
        ? {
            id: quote.id,
            createdAt: quote.createdAt instanceof Date ? quote.createdAt.toISOString() : quote.createdAt,
            createdByUser: quote.createdByUser
              ? {
                  firstName: quote.createdByUser.firstName,
                  lastName: quote.createdByUser.lastName,
                }
              : undefined,
          }
        : undefined,
    },
    { filter, sorting, textInput },
  );

  // Auto-fetch more data when scrolled to bottom
  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreDocuments &&
      allDocuments &&
      searchDocuments?.pages[0]?.pagination?.total &&
      searchDocuments?.pages[0]?.pagination?.total > allDocuments.length
    ) {
      fetchMoreDocuments();
    }
  }, [allDocuments, fetchMoreDocuments, isFetchingMoreDocuments, isScrolledToBottom, searchDocuments]);

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreNotes &&
      allNotes &&
      searchNotes?.pages[0]?.pagination?.total &&
      searchNotes?.pages[0]?.pagination?.total > allNotes.length
    ) {
      fetchMoreNotes();
    }
  }, [allNotes, fetchMoreNotes, isFetchingMoreNotes, isScrolledToBottom, searchNotes]);

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreQuoteRatings &&
      allQuoteRatings &&
      searchQuoteRatings?.pages[0]?.pagination?.total &&
      searchQuoteRatings?.pages[0]?.pagination?.total > allQuoteRatings.length
    ) {
      fetchMoreQuoteRatings();
    }
  }, [allQuoteRatings, fetchMoreQuoteRatings, isFetchingMoreQuoteRatings, isScrolledToBottom, searchQuoteRatings]);

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreStatusChanges &&
      allStatusChanges &&
      quoteStatusChange?.pages[0]?.pagination?.total &&
      quoteStatusChange?.pages[0]?.pagination?.total > allStatusChanges.length
    ) {
      fetchMoreStatusChanges();
    }
  }, [allStatusChanges, fetchMoreStatusChanges, isFetchingMoreStatusChanges, isScrolledToBottom, quoteStatusChange]);

  useEffect(() => {
    if (
      isScrolledToBottom &&
      !isFetchingMoreStatusChanges &&
      allQuoteUnderwritingActivity &&
      quoteUnderwritingActivity?.pages[0]?.pagination?.total &&
      quoteUnderwritingActivity?.pages[0]?.pagination?.total > allQuoteUnderwritingActivity.length
    ) {
      fetchMoreQuoteUnderwritingActivity();
    }
  }, [
    allQuoteUnderwritingActivity,
    fetchMoreQuoteUnderwritingActivity,
    isFetchingMoreQuoteUnderwritingActivity,
    isScrolledToBottom,
    quoteUnderwritingActivity,
    isFetchingMoreStatusChanges,
  ]);

  return {
    quoteId,
    quote,
    sorting,
    filter,
    textInput,
    isScrolledToBottom,
    isLoading,
    isFetchingMore,
    allStatusChanges,
    allQuoteRatings,
    allNotes,
    allDocuments,
    items,
    dateHeaderItems,
    lastLoadedItems,
    setSorting,
    setFilter,
    setTextInput,
    setIsScrolledToBottom,
    fetchMoreStatusChanges,
    fetchMoreQuoteRatings,
    fetchMoreDocuments,
    fetchMoreNotes,
  };
};
